/**
 * FindingDetail page (P70.2). Route: /twin/findings/:findingId.
 *
 * Composes:
 *   header strip   -- title + severity badge + status pill
 *   metadata grid  -- external_ref + raised_at + closed_at +
 *                     impacted entity link
 *   description    -- raw text
 *   View source    -- external_url link when present
 *   payload viewer -- collapsible <pre> JSON block
 *
 * Read-only at v0.72.0. Findings are connector-owned; mutations
 * belong on CAPs (P10).
 */
import { OwnerIncidentStrip, ownerStripApplies } from "twin/finding/OwnerIncidentStrip";
import { useMemo, useState } from "react";
import { i18n } from "shared/i18n";
import { formatDateTime } from "shared/time/formatDate";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { findingCategoryLabel, stateLabel } from "shared/vocab/workflowState";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { entityKindLabelFor } from "shared/vocab/entityKind";
import { canRunOperations } from "shared/api/authority";
import { hasPosition, useMe } from "shared/api/useMe";
import { hasModule } from "shared/api/authority";
import { FindingCommentBox } from "twin/finding/FindingCommentBox";
import { useToast } from "shared/toast/ToastProvider";
import { IncidentReportingPanel } from "./IncidentReportingPanel";
import { ThreadStrip } from "shared/thread/ThreadStrip";
import { useDownloadIncidentReport } from "./useDownloadIncidentReport";
import { useDraftCapFromFinding } from "./useDraftCapFromFinding";
import type { Finding, FindingLinkedCap } from "shared/api/types";
import { entityDetailPath } from "shared/routes/entityRoutes";
import { useFindingDetail } from "twin/finding/useFindingDetail";
import { useFindingLinkedCAPs } from "twin/finding/useFindingLinkedCAPs";

import { useMyEstate } from "twin/home/useMyEstate";
const CAP_STATE_STYLES: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700",
  in_review: "bg-amber-50 text-amber-800",
  approved: "bg-emerald-50 text-emerald-700",
  sent_back: "bg-rose-50 text-rose-700",
};

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-900 border-red-200",
  high: "bg-amber-100 text-amber-900 border-amber-200",
  medium: "bg-slate-100 text-slate-700 border-slate-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};

const STATUS_STYLES: Record<string, string> = {
  open: "bg-rose-50 text-rose-700",
  closed: "bg-emerald-50 text-emerald-700",
};

// P272.1: kind->route mapping lifted to shared/routes/entityRoutes.ts
// (shared with the cascade impact cards). The old local map linked
// product to a nonexistent /twin/product/:id route; entityDetailPath
// returns null for product, so it now renders as plain text.

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "--";
  try {
    return formatDateTime(value, i18n.language);
  } catch {
    return value;
  }
}

function severityClass(severity: string): string {
  return SEVERITY_STYLES[severity] ?? "bg-slate-100 text-slate-700 border-slate-200";
}

function statusClass(status: string): string {
  return STATUS_STYLES[status] ?? "bg-slate-100 text-slate-700";
}

interface EntityLinkProps {
  finding: Finding;
}

function EntityLink({ finding }: EntityLinkProps) {
  const { t } = useTranslation();
  const path = entityDetailPath(finding.entity_kind, finding.entity_id);
  if (!path) {
    return (
      <span data-testid="finding-entity-link-noop" className="text-sm text-slate-500">
        {entityKindLabelFor(finding.entity_kind, finding.entity_service_class)} (
        {finding.entity_id.slice(0, 8)}...)
      </span>
    );
  }
  return (
    <Link
      to={path}
      data-testid="finding-entity-link"
      className="text-sm font-medium text-brand-navy hover:text-brand-brass"
    >
      {t("screens.findingDetail.view")}{" "}
      {entityKindLabelFor(finding.entity_kind, finding.entity_service_class)}{" "}
      {t("screens.findingDetail.rarr")}
    </Link>
  );
}

interface PayloadViewerProps {
  payload: Record<string, unknown>;
}

function PayloadViewer({ payload }: PayloadViewerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const json = useMemo(() => JSON.stringify(payload, null, 2), [payload]);
  return (
    <section data-testid="finding-payload" className="space-y-2">
      <header className="flex items-baseline gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.findingDetail.connectorPayload")}
        </h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="finding-payload-toggle"
          className="text-xs text-slate-600 hover:text-brand-brass underline"
        >
          {open ? t("screens.findingDetail.hide") : t("screens.findingDetail.show")}
        </button>
        {open && (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(json);
            }}
            data-testid="finding-payload-copy"
            className="text-xs text-slate-600 hover:text-brand-brass underline"
          >
            {t("screens.findingDetail.copy")}
          </button>
        )}
      </header>
      {open && (
        <pre
          data-testid="finding-payload-pre"
          className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-auto max-h-96"
        >
          {json}
        </pre>
      )}
    </section>
  );
}

/**
 * B6 -- the operator's facts, folded for everybody else.
 *
 * An operator keeps the grid open: a source system and a connector
 * handle are how he finds the row in the tool that raised it. For the
 * person accountable for the service they sit between him and the two
 * things he came for, which is why he read this page four rounds
 * running as somebody else's.
 */
function FindingMeta({
  isOperator,
  children,
}: {
  isOperator: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  if (isOperator) return <>{children}</>;
  return (
    /* D7 (round 8) -- this summary appeared TWICE on one page, once
       above the description and once below the linked CAPs, because
       both disclosures used the same string. Two identical "Technical
       detail" folds read as the page repeating itself, and neither
       said what was inside. They hold different things. */
    <details data-testid="finding-meta-collapsed">
      <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
        {t("modules.twin.finding.operatorDetailMeta")}
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

function LinkedCAPsSection({
  findingId,
  isOperator,
}: {
  findingId: string;
  isOperator: boolean;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const mayRead = hasModule(me.data, "framework");
  const linked = useFindingLinkedCAPs(findingId, mayRead);

  // Twin-only viewers (no framework licence) get a 403; degrade to a
  // quiet note rather than an error.
  //
  // D6 -- and say something USEFUL to the person who will never have
  // that access. An owner met "Linked CAPs require framework access",
  // which is the product describing its own licensing where he needed
  // to know where remediation is kept. The operator keeps the original
  // wording, because for him a missing licence is worth acting on.
  if (!mayRead || (linked.isError && (linked.error?.status ?? 0) === 403)) {
    return (
      <section id="linked-caps" data-testid="finding-linked-caps" className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.finding.linkedCaps.heading")}
        </h2>
        <p
          data-testid="finding-linked-caps-degraded"
          className="text-sm text-slate-500"
        >
          {isOperator
            ? t("modules.twin.finding.linkedCaps.needsFramework")
            : t("modules.twin.finding.linkedCaps.keptInFramework")}
        </p>
      </section>
    );
  }

  const rows = Array.isArray(linked.data) ? linked.data : [];
  return (
    <section id="linked-caps" data-testid="finding-linked-caps" className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("modules.twin.finding.linkedCaps.heading")}
      </h2>
      {linked.isLoading ? (
        <p className="text-sm text-slate-500">
          {t("modules.twin.finding.linkedCaps.loading")}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="finding-linked-caps-empty" className="text-sm text-slate-500">
          {t("modules.twin.finding.linkedCaps.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((cap: FindingLinkedCap) => (
            <li
              key={cap.link_id}
              data-testid="finding-linked-cap-row"
              data-cap-id={cap.cap_id}
              className="flex items-center justify-between gap-3 py-1.5"
            >
              <Link
                to={`/framework/caps/${cap.cap_id}`}
                className="text-sm font-medium text-brand-navy hover:text-brand-brass truncate"
              >
                {cap.cap_title}
                {" · "}
                <span className="ms-2 font-mono text-xs text-slate-400">
                  {cap.regulator_code}
                </span>
              </Link>
              <span
                className={[
                  "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium",
                  CAP_STATE_STYLES[cap.state] ?? "bg-slate-100 text-slate-700",
                ].join(" ")}
                data-testid={`finding-linked-cap-state-${cap.cap_id}`}
              >
                {/* Round 19 H55 -- it printed `in_review`. */}
                {stateLabel(cap.state)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Round 19 A45 -- a reference MARSAD minted itself, in words. "demo-
 *  hydrate:..." sat above a finding's title as if it were a handle on
 *  somebody's system; it is how the finding came to be. A vendor's own
 *  reference (an incident number) is shown as it is. */
const MINTED_REFS: readonly [string, string][] = [
  ["manual:", "modules.twin.finding.raisedByHand"],
  ["demo-hydrate:", "modules.twin.finding.ref.demoSeed"],
  ["demo-harness:", "modules.twin.finding.ref.demoSeed"],
  ["demo:", "modules.twin.finding.ref.demoSeed"],
  ["telemetry-auto:", "modules.twin.finding.ref.telemetry"],
  ["tolerance-breach:", "modules.twin.finding.ref.toleranceBreach"],
  ["change:", "modules.twin.finding.ref.change"],
];

export function refInWords(ref: string, t: (key: string) => string): string | null {
  const hit = MINTED_REFS.find(([prefix]) => ref.startsWith(prefix));
  return hit ? t(hit[1]) : null;
}

export function FindingDetail() {
  const { t } = useTranslation();
  const { findingId } = useParams<{ findingId: string }>();
  const detail = useFindingDetail(findingId);
  const me = useMe();
  // D7 -- read with the other hooks, used after the finding loads.
  // Hooks cannot sit behind the early returns below.
  const estate = useMyEstate();
  const toast = useToast();
  const downloadReport = useDownloadIncidentReport();
  const draftCap = useDraftCapFromFinding();
  const navigate = useNavigate();

  if (detail.isLoading) {
    return (
      <p data-testid="finding-loading" className="text-sm text-slate-500">
        {t("modules.twin.finding.loading")}
      </p>
    );
  }
  if (detail.isError) {
    const status = detail.error?.status ?? 0;
    if (status === 404) {
      return (
        <article data-testid="finding-not-found" className="space-y-2">
          <h1 className="text-2xl font-semibold text-slate-900">
            <MarsadIcon
              name="finding"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("modules.twin.finding.notFoundHeading")}
          </h1>
          <p className="text-sm text-slate-600">
            {t("modules.twin.finding.notFoundBody", { id: findingId })}
          </p>
        </article>
      );
    }
    return (
      <p data-testid="finding-error" className="text-sm text-rose-700">
        {t("modules.twin.finding.error")}
      </p>
    );
  }
  if (!detail.data) {
    return null;
  }
  const finding = detail.data;
  // E4 -- whose page this is. An operator keeps every section open;
  // for an owner the operator detail folds away.
  // D7 -- whether he OWNS this record, not what he holds elsewhere.
  //
  // Sixth round, and the reason it kept recurring is that the gate was
  // right in principle and asked the wrong question. It reads global
  // positions, and the Service Owner holds `preparer` because he raises
  // findings and attests -- so he was an "operator" on his own
  // service's incident page and got the operator's view: a raw
  // manual: handle, the connector wording, a licensing sentence about
  // framework access.
  //
  // A page adapts to the reader's relationship to THIS record. Owning
  // the thing the finding is on makes him the owner here, whatever else
  // he does in the product.
  const ownsThis =
    finding.entity_id != null &&
    [...(estate.data?.services ?? []), ...(estate.data?.ibses ?? [])].some(
      (e) => e.id === finding.entity_id,
    );

  /* G3 -- does the owner strip render? If it does the comment box
     goes inside it, at the top of the page, instead of a screen and a
     half down. */
  const strip = ownerStripApplies(finding, estate.data?.ibses);
  /* Round 19 G35 -- a finding a significant change raised is not an
     incident: no customer question, no incident record, no regulator
     report; it says which change raised it. */
  const changeId =
    finding.source_system_code === "change.event" &&
    typeof finding.payload?.["change_event_id"] === "string"
      ? (finding.payload["change_event_id"] as string)
      : null;
  const whatYouDoKey = changeId
    ? "modules.twin.finding.whatYouDoChange"
    : finding.entity_service_class === "internal"
      ? "modules.twin.finding.whatYouDoInternal"
      : "modules.twin.finding.whatYouDo";
  const ownerComment = strip ? (
    <div className="w-full">
      <p
        data-testid="finding-what-you-do"
        className="text-xs font-medium text-brand-navy"
      >
        {t(whatYouDoKey)}
      </p>
      <FindingCommentBox
        findingId={finding.id}
        audience={finding.comment_audience ?? 0}
        audienceNames={finding.comment_audience_names ?? []}
        internal={finding.entity_service_class === "internal"}
        change={changeId !== null}
      />
    </div>
  ) : null;

  const isOperator =
    !ownsThis &&
    (canRunOperations(me.data) ||
      hasPosition(me.data, "preparer") ||
      hasPosition(me.data, "reviewer") ||
      hasPosition(me.data, "approver"));

  return (
    <article data-testid="finding-detail" className="space-y-6">
      {/* D6 -- for the person accountable for the service, the two
          questions are how long and when does it breach. The operator
          sections stay below, unchanged. */}
      <OwnerIncidentStrip
        finding={finding}
        commentSlot={ownerComment}
        mayReadPlans={hasModule(me.data, "framework")}
      />
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <span
            data-testid="finding-severity"
            data-severity={finding.severity}
            className={[
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
              severityClass(finding.severity),
            ].join(" ")}
          >
            {finding.severity}
          </span>
          <span
            data-testid="finding-status"
            data-status={finding.status}
            className={[
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium uppercase tracking-wide",
              statusClass(finding.status),
            ].join(" ")}
          >
            {finding.status}
          </span>
          {changeId && (
            <Link
              to={`/twin/changes?change=${changeId}`}
              data-testid="finding-raised-by-change"
              className="inline-flex items-center rounded-full border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:underline"
            >
              {t("modules.twin.finding.raisedByChange")}
            </Link>
          )}
          {finding.source_system_code === "telemetry.auto" && (
            <span
              data-testid="finding-telemetry-badge"
              className="inline-flex items-center rounded-full border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark"
            >
              {t("modules.twin.finding.telemetryRaised")}
            </span>
          )}
          {/* D6 -- "manual:" and a hex string is an operator's handle
              on a row in somebody else's system. To the person
              accountable for the service it is noise above the two
              facts he came for, and the title already says which
              incident this is. */}
          {isOperator && (
            /* G3 -- an operator handle with no break opportunity took
               the page 36px sideways on a phone. */
            /* Round 16 H25 -- an operator-raised finding's ref is
               `manual:` and a uuid MARSAD minted; it is no handle on
               anybody's system, so it says how the finding came to be.
               The ref itself stays in the title, unchanged. */
            <code
              data-testid="finding-external-ref"
              title={finding.external_ref}
              className="break-all text-xs text-slate-500"
            >
              {refInWords(finding.external_ref, t) ?? finding.external_ref}
            </code>
          )}
        </div>
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="finding"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {finding.title}
        </h1>
        {/* D7 (round 8) -- somebody is on this.
            The page named NOBODY as being on the incident. For the
            operator that is fine, he is the one on it; the service
            owner who opened it from his bell was shown a record of
            something going wrong with no person anywhere on it. This
            is not workflow on the finding -- the lean shape is a P2
            lock and the human workflow lives on the CAP that responds.
            It is who the estate already records as accountable. */}
        {finding.entity_owner_name && (
          <p
            data-testid="finding-accountable-owner"
            className="w-full text-xs text-slate-600"
          >
            {t("modules.twin.finding.accountable", {
              who: finding.entity_owner_name,
            })}
          </p>
        )}
        {/* Round 9 C4, third round. The page named him, asked nothing of
            him and carried no control of any kind -- the one person it
            exists for could only read it. */}
        {/* Round 10 C1 -- resolved through the CI and business-service
            reroute now, so it fills on the incidents people actually
            open. One team is a name; several is a different sentence. */}
        {(finding.responding_teams ?? []).length > 0 && (
          <p
            data-testid="finding-responding-team"
            data-count={(finding.responding_teams ?? []).length}
            className="w-full text-xs text-slate-600"
          >
            {(finding.responding_teams ?? []).length === 1
              ? t("modules.twin.finding.whoIsOnIt", {
                  team: finding.responding_teams![0],
                })
              : t("modules.twin.finding.whoIsOnItMany", {
                  teams: (finding.responding_teams ?? []).join(", "),
                })}
          </p>
        )}
        <WhoWasTold finding={finding} />
        {/* ROUND 13 G3 -- on a phone this sat 1.34 screens below the
            fold, further than round 12 measured it, behind the strip,
            the chips, the title, the accountable owner and the
            responding team. For the owner it now rides inside the
            strip at the top of the page; for the operator, whose page
            this already suits, nothing moves. */}
        {finding.status === "open" && !strip && (
          <div className="w-full">
            <p
              data-testid="finding-what-you-do"
              className="text-xs font-medium text-brand-navy"
            >
              {t(whatYouDoKey)}
            </p>
            <FindingCommentBox
              findingId={finding.id}
              audience={finding.comment_audience ?? 0}
              audienceNames={finding.comment_audience_names ?? []}
              internal={finding.entity_service_class === "internal"}
              change={changeId !== null}
            />
          </div>
        )}
        {!changeId &&
          (canRunOperations(me.data) || hasPosition(me.data, "approver")) && (
            <button
              type="button"
              data-testid="finding-incident-report-download"
              disabled={downloadReport.isPending}
              onClick={() =>
                downloadReport.mutate(
                  { findingId: finding.id },
                  {
                    onSuccess: ({ filename }) =>
                      toast.push({
                        tone: "success",
                        message: t("modules.twin.finding.incidentReport.done", {
                          filename,
                        }),
                      }),
                    onError: (err) =>
                      toast.push({ tone: "error", message: err.detail }),
                  },
                )
              }
              className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
            >
              {downloadReport.isPending
                ? t("modules.twin.finding.incidentReport.downloading")
                : t("modules.twin.finding.incidentReport.download")}
            </button>
          )}
        {/* A6: "we found this" to "here is what we are doing about it"
            had no path -- an existing CAP could be linked to a finding
            and nothing more. Drafting is not approving: the CAP lands
            in draft at the preparer position. */}
        {canRunOperations(me.data) && (
          <button
            type="button"
            data-testid="finding-draft-cap"
            disabled={draftCap.isPending}
            onClick={() =>
              draftCap.mutate(
                { findingId: finding.id },
                {
                  onSuccess: (res) => {
                    toast.push({
                      tone: "success",
                      message: t("modules.twin.finding.draftCap.done"),
                    });
                    navigate(`/framework/caps/${res.cap_id}`);
                  },
                  onError: (err) => toast.push({ tone: "error", message: err.detail }),
                },
              )
            }
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
          >
            {draftCap.isPending
              ? t("modules.twin.finding.draftCap.working")
              : t("modules.twin.finding.draftCap.action")}
          </button>
        )}
      </header>
      {/* Stitching review 5.2 -- the incident's thread: the reporting
          duty, where it lands, what was judged, the plans answering it,
          and what can be proved. */}
      <ThreadStrip subject="finding" id={finding.id} />

      {/* B6 -- fourth round. Two good strips, and then the operator's
          page: a source-system label, a connector handle, a view-source
          link into somebody else's tool. None of it is wrong; none of
          it is his. The strip above answers his questions, so the rest
          folds beneath a summary he can open if he wants it -- the
          same treatment the payload already had, applied to the row of
          facts that sits between him and it. */}
      <FindingMeta isOperator={isOperator}>
        <dl
          data-testid="finding-meta"
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm"
        >
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("modules.twin.finding.raisedAt")}
            </dt>
            <dd className="text-slate-900">{formatTimestamp(finding.raised_at)}</dd>
            {finding.category && (
              <>
                <dt className="text-slate-500">
                  {t("screens.findingDetail.category")}
                </dt>
                <dd>
                  <span
                    data-testid="finding-category"
                    className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-900"
                  >
                    {findingCategoryLabel(finding.category)}
                  </span>
                </dd>
              </>
            )}
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("modules.twin.finding.closedAt")}
            </dt>
            <dd className="text-slate-900">{formatTimestamp(finding.closed_at)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("modules.twin.finding.impactedEntity")}
            </dt>
            <dd>
              <EntityLink finding={finding} />
            </dd>
          </div>
          {(finding.raised_by_name || finding.source_system_label) && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">
                {t("modules.twin.finding.raisedBy")}
              </dt>
              {/* Round 14 G, eighth round -- the person first, the form
                  they used second. "Manual intake" named a channel, and
                  a finding somebody raised by hand has somebody. */}
              <dd data-testid="finding-raised-by" className="text-slate-900">
                {finding.raised_by_name ? (
                  <>
                    {finding.raised_by_name}
                    {finding.source_system_label && (
                      <span className="text-slate-500">
                        {" "}
                        {t("modules.twin.finding.raisedVia", {
                          source: finding.source_system_label,
                        })}
                      </span>
                    )}
                  </>
                ) : (
                  finding.source_system_label
                )}
              </dd>
            </div>
          )}
          {/* Round 19 H53 -- the cell read SOURCE "--" on a finding a
              significant change raised: it holds a link into the source
              system, and a finding raised inside the product has none.
              Labelled for what it holds, and absent when there is none. */}
          {finding.external_url && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">
                {t("modules.twin.finding.sourceLinkLabel")}
              </dt>
              <dd>
                <a
                  href={finding.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="finding-source-link"
                  className="text-sm font-medium text-brand-navy hover:text-brand-brass"
                >
                  {t("modules.twin.finding.viewSourceLink")}{" "}
                  {t("screens.findingDetail.rarr")}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </FindingMeta>

      {/* Round 11 E1 -- `dir` on the labelled section too. The
          paragraph has carried `dir="auto"` since round 10; the section
          holding the test id had none, so the measurement asked the
          element it could name and got null. The section resolves from
          its own heading, which is in the reader's language; the
          paragraph still resolves from the connector's words, which is
          the answer that matters. */}
      {finding.description && (
        <section dir="auto" data-testid="finding-description" className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.finding.descriptionHeading")}
          </h2>
          {/* Round 10 E1: a connector's own words, in whatever
              language the source system wrote them. */}
          <p dir="auto" className="text-sm text-slate-800 whitespace-pre-line">
            {finding.description}
          </p>
        </section>
      )}

      {/* P422 -- the stages reported to the regulator, operators only. */}
      {isOperator && !changeId && (
        <IncidentReportingPanel
          findingId={finding.id}
          canRecord={canRunOperations(me.data) || hasPosition(me.data, "approver")}
        />
      )}
      <LinkedCAPsSection findingId={finding.id} isOperator={isOperator} />

      {/* E4 -- the connector payload is an operator's artefact. For
          the person accountable for the service it is a wall of
          somebody else's JSON beneath the two facts he came for, so it
          folds away unless he asks. An operator sees it as before. */}
      {isOperator ? (
        <PayloadViewer payload={finding.payload} />
      ) : (
        <details data-testid="finding-payload-collapsed">
          <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
            {t("modules.twin.finding.operatorDetailPayload")}
          </summary>
          <div className="mt-2">
            <PayloadViewer payload={finding.payload} />
          </div>
        </details>
      )}
    </article>
  );
}

/** Round 20 U2 -- who knows. Asked during window I, the product had no
 *  answer: no notification log, no acknowledgement. Every notice that
 *  links to the finding is listed, with when, how and whether it was
 *  acknowledged; and where nobody was told, the page says so. */
function WhoWasTold({ finding }: { finding: Finding }) {
  const { t } = useTranslation();
  const told = finding.told ?? [];
  const held = finding.told_held ?? 0;
  return (
    <div
      data-testid="finding-told"
      data-count={told.length}
      className="w-full text-xs text-slate-600"
    >
      <p className="font-medium text-brand-navy">
        {t("modules.twin.finding.toldHeading")}
      </p>
      {told.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {told.map((row) => (
            <li
              key={`${row.name}-${row.told_at}-${row.kind}`}
              data-testid="finding-told-row"
            >
              {t("modules.twin.finding.toldRow", {
                name: row.name,
                at: formatTimestamp(row.told_at),
                how: row.emailed
                  ? t("modules.twin.finding.toldByBellAndEmail")
                  : t("modules.twin.finding.toldByBell"),
              })}
              {" -- "}
              {row.acknowledged_at
                ? t("modules.twin.finding.toldAcknowledged", {
                    at: formatTimestamp(row.acknowledged_at),
                  })
                : t("modules.twin.finding.toldNotAcknowledged")}
            </li>
          ))}
        </ul>
      ) : held === 0 ? (
        <p data-testid="finding-told-nobody">
          {finding.severity === "critical"
            ? t("modules.twin.finding.toldNobodyCritical")
            : t("modules.twin.finding.toldNobodyBySeverity", {
                severity: finding.severity,
              })}
        </p>
      ) : null}
      {held > 0 && (
        <p data-testid="finding-told-held">
          {t("modules.twin.finding.toldHeld", { count: held })}
        </p>
      )}
    </div>
  );
}
